import { Button } from '@parrot-software/design-system';
import { RefreshCw } from '@parrot-software/design-system/icons';
import { cn } from '@parrot-software/utils';

/** Props for ConfiguringState component. */
export type ConfiguringStateProps = {
  description?: string;
  isRefreshing?: boolean;
  onRefresh?: () => void;
  refreshLabel?: string;
  title: string;
};

/**
 * ConfiguringState - Global "configuring" state for subscription blocks.
 *
 * Shown when a block's async backend configuration (`configEnabled === false`) is
 * still running and data is stale/unsafe to act on. Displays a large yellow
 * refresh icon, a title/description, and a manual "Actualizar" (re-fetch) action,
 * mirroring the empty/error state surfaces but conveying an in-progress config.
 * While `isRefreshing` is true the action is disabled and its icon spins.
 */
export const ConfiguringState = ({
  title,
  description,
  isRefreshing = false,
  onRefresh,
  refreshLabel,
}: ConfiguringStateProps) => (
  <div
    className="flex flex-col h-[50vh] items-center justify-center gap-6 rounded-xl border p-12 shadow-xs"
    data-testid="subscription-configuring-state"
  >
    <div className="flex flex-col items-center gap-3 max-w-[350px]">
      <RefreshCw className="h-9 w-9 text-primary" strokeWidth={1.5} />
      <p className="text-lg font-medium text-center">{title}</p>
      {description && <p className="text-sm text-muted-foreground text-center">{description}</p>}
    </div>

    {onRefresh && (
      <Button disabled={isRefreshing} onClick={onRefresh}>
        <RefreshCw className={cn('mr-2 h-4 w-4', { 'animate-spin': isRefreshing })} />
        {refreshLabel}
      </Button>
    )}
  </div>
);
